import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ReferenceDataTabs,
  ReferenceDataTabsContent,
  ReferenceDataTabsList,
  ReferenceDataTabsTrigger,
} from "@/components/admin/reference-data/ReferenceDataTabs";
import { CompactHero } from "@/components/brand/CompactHero";
import {
  Empty,
  ErrorBlock,
  Loading,
  StaleNotice,
} from "@/components/admin/reference-data/shared";
import { HeroMetaItem } from "@/components/brand/Hero";
import { ConfigTypeTableTab } from "@/components/admin/reference-data/ConfigTypeTableTab";
import { PackageReleasesPanel } from "@/components/admin/reference-data/PackageReleasesPanel";
import { PackageUploadPanel } from "@/components/admin/reference-data/PackageUploadPanel";
import {
  useConfigPackage,
  useReferenceDataAgents,
  useReferenceDataSources,
} from "@/hooks/useApi";
import {
  activeReleaseLabel,
  agentPath,
} from "@/lib/reference-data-config-types";
import { formatRelative } from "@/lib/datetime";
import { sourceLabels } from "@/lib/reference-data-labels";

export default function AdminReferenceDataConfigType() {
  const { agentKey = "", typeKey = "" } = useParams<{
    agentKey: string;
    typeKey: string;
  }>();
  // A chave zera o rascunho do upload ao trocar de tipo: a rota é a mesma e o
  // React reaproveitaria o estado do tipo anterior.
  return (
    <ConfigTypePage
      key={`${agentKey}/${typeKey}`}
      agentKey={agentKey}
      typeKey={typeKey}
    />
  );
}

function ConfigTypePage({
  agentKey,
  typeKey,
}: {
  agentKey: string;
  typeKey: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useReferenceDataAgents();
  const { data: sourcesData } = useReferenceDataSources();
  const agent = data?.agents.find((a) => a.key === agentKey);
  const configType = agent?.config_types.find((c) => c.key === typeKey);
  const labelOf = (table: string) =>
    sourceLabels(
      t,
      sourcesData?.sources.find((s) => s.table_name === table),
    ).displayName || table;

  // O rascunho do upload vive na página: o Radix desmonta a aba inativa.
  const [file, setFile] = useState<File | null>(null);
  const [packageId, setPackageId] = useState<string | undefined>(undefined);
  // "Enviando" também fica aqui: o painel desmonta ao trocar de aba e o botão reabilitaria.
  const [uploading, setUploading] = useState(false);
  // O erro do envio também: se ele falhar com outra aba aberta, a volta ainda o mostra.
  const [runError, setRunError] = useState<string | null>(null);
  // Observador único do pacote: invalida versões e auditoria das tabelas quando ele sai de
  // running, com qualquer aba aberta. O painel de upload lê a mesma consulta sem invalidar.
  const tables = useMemo(
    () => configType?.tables.map((x) => x.table_name),
    [configType?.tables],
  );
  useConfigPackage(typeKey, packageId, tables);
  const firstTable = configType?.tables[0]?.table_name ?? "upload";
  // A aba vive na URL (?tab=): os links das versões abrem direto nas Releases.
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab");
  const knownTabs = [
    "upload",
    "releases",
    ...(configType?.tables.map((x) => x.table_name) ?? []),
  ];
  const activeTab =
    requestedTab && knownTabs.includes(requestedTab)
      ? requestedTab
      : firstTable;
  const setTab = (next: string) =>
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        merged.set("tab", next);
        return merged;
      },
      { replace: true },
    );

  const release = configType?.active_release;
  const kpis = [
    {
      label: t("Active release"),
      value: (configType && activeReleaseLabel(configType)) ?? "—",
      gold: true,
    },
    {
      label: t("Promoted"),
      value: release?.promoted_at ? formatRelative(release.promoted_at) : "—",
      sub: release?.promoted_by
        ? `${t("by")} ${release.promoted_by}`
        : undefined,
    },
  ];

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<Boxes />}
        overline={
          agent
            ? `${t(agent.display_name_key)} · ${t("CONFIGURATION TYPE")}`
            : t("REFERENCE DATA · AGENT")
        }
        title={
          configType ? t(configType.display_name_key) : t("Configuration type")
        }
        meta={
          configType ? (
            <HeroMetaItem k={t("Source")} v={t(configType.description_key)} />
          ) : undefined
        }
        actions={
          <Button
            variant="ghost-light"
            size="sm"
            className="h-9 gap-2 px-3.5 text-xs"
            onClick={() => navigate(agentPath(agentKey))}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t("Back to agent")}
          </Button>
        }
        kpis={configType ? kpis : undefined}
      />
      <div className="p-8">
        {isLoading ? (
          <div role="status">
            <Loading />
          </div>
        ) : isError && !data ? (
          <ErrorBlock
            message={t("Could not load reference data agents.")}
            onRetry={() => void refetch()}
          />
        ) : !configType ? (
          <Empty text={t("Configuration type not found.")} />
        ) : (
          <>
            {isError && <StaleNotice />}
            <ReferenceDataTabs value={activeTab} onValueChange={setTab}>
              <ReferenceDataTabsList>
                {configType.tables.map((table) => (
                  <ReferenceDataTabsTrigger
                    key={table.table_name}
                    value={table.table_name}
                  >
                    {labelOf(table.table_name)}
                  </ReferenceDataTabsTrigger>
                ))}
                <ReferenceDataTabsTrigger value="upload">
                  {t("Upload SAP file")}
                </ReferenceDataTabsTrigger>
                <ReferenceDataTabsTrigger value="releases">
                  {t("Releases")}
                </ReferenceDataTabsTrigger>
              </ReferenceDataTabsList>
              {configType.tables.map((table) => (
                <ReferenceDataTabsContent
                  key={table.table_name}
                  value={table.table_name}
                  className="mt-5"
                >
                  <ConfigTypeTableTab
                    tableName={table.table_name}
                    onOpenPackageReleases={() => setTab("releases")}
                  />
                </ReferenceDataTabsContent>
              ))}
              <ReferenceDataTabsContent value="upload" className="mt-5">
                <PackageUploadPanel
                  configType={configType}
                  labelOf={labelOf}
                  file={file}
                  setFile={setFile}
                  packageId={packageId}
                  setPackageId={setPackageId}
                  uploading={uploading}
                  setUploading={setUploading}
                  runError={runError}
                  setRunError={setRunError}
                />
              </ReferenceDataTabsContent>
              <ReferenceDataTabsContent value="releases" className="mt-5">
                <PackageReleasesPanel
                  configType={configType}
                  labelOf={labelOf}
                />
              </ReferenceDataTabsContent>
            </ReferenceDataTabs>
          </>
        )}
      </div>
    </div>
  );
}
